import { useState } from 'react';
import { Typography } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { STATUS_CONFIG } from './constants';
import BookingQuickActions from './BookingQuickActions';
import GuestCustomerBadge from '../../shared/GuestCustomerBadge';
import { PaymentStatusBadge } from '../../shared/PaymentStatusBadge';
import { AttendanceStatusBadge } from '../../shared/AttendanceStatusBadge';
import type { Booking } from '../../../types/provider';

const { Text } = Typography;

function formatTime(iso: string): string {
  return iso.substring(11, 16);
}

function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('998')) {
    return `+${digits.slice(0, 3)} ${digits.slice(3, 5)} ${digits.slice(5, 8)} ${digits.slice(8, 10)} ${digits.slice(10)}`;
  }
  if (digits.length === 9) {
    return `+998 ${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 7)} ${digits.slice(7)}`;
  }
  return raw;
}

function isPhone(value: string): boolean {
  return /^[\d\s+\-()]{7,}$/.test(value.trim()) && /\d{7,}/.test(value.replace(/\D/g, ''));
}

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

interface Props {
  booking: Booking;
  onOpenDetail: (booking: Booking) => void;
}

export default function BookingCard({ booking, onOpenDetail }: Props) {
  const { t } = useTranslation(['common', 'provider']);
  const [isActionOpen, setIsActionOpen] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const config = STATUS_CONFIG[booking.status] ?? STATUS_CONFIG['PENDING'];
  const statusLabel = t(`provider:bookings.statusLabels.${booking.status}`, {
    defaultValue: booking.status,
  });
  const isCanceled =
    booking.status === 'CANCELED_BY_USER' ||
    booking.status === 'CANCELED_BY_SERVICE_PROVIDER';
  const isCompleted = booking.status === 'COMPLETED';
  const isPending = booking.status === 'PENDING';
  const cancellationReasonDisplay = booking.cancellationReasonText ?? booking.cancellationReason;

  const clientDisplay =
    booking.clientName && isPhone(booking.clientName)
      ? formatPhone(booking.clientName)
      : booking.clientName;
  const avatarLetters = clientDisplay ? getInitials(clientDisplay) : '?';

  return (
    <div
      style={{
        position: 'relative',
        borderRadius: 16,
        background: 'var(--bl-surface, #fff)',
        boxShadow: isPressed
          ? '0 1px 4px rgba(0,0,0,0.04)'
          : 'var(--bl-shadow-soft, 0 2px 10px rgba(0,0,0,0.06))',
        margin: '0 16px 10px',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'transform 0.12s ease, box-shadow 0.12s ease',
        transform: isPressed ? 'scale(0.985)' : 'scale(1)',
        WebkitTapHighlightColor: 'transparent',
        opacity: isCanceled ? 0.78 : 1,
      }}
      onClick={() => { if (!isActionOpen) onOpenDetail(booking); }}
      onTouchStart={() => setIsPressed(true)}
      onTouchEnd={() => setIsPressed(false)}
      onTouchCancel={() => setIsPressed(false)}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onMouseLeave={() => setIsPressed(false)}
    >
      {/* Top status strip — subtle gradient */}
      <div
        style={{
          height: 3,
          background: `linear-gradient(90deg, ${config.color} 0%, ${config.color}66 100%)`,
        }}
      />

      <div style={{ padding: '12px 14px 4px' }}>
        {/* ─── Row 1: Time + status pill ────────────────────────────── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <ClockCircleOutlined style={{ fontSize: 12, color: config.color, flexShrink: 0 }} />
            <Text
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: 'var(--bl-text-strong, #0f172a)',
                letterSpacing: -0.2,
                lineHeight: 1.2,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatTime(booking.startTime)}
              <span style={{ fontWeight: 400, color: 'var(--bl-text-muted, #9ca3af)', margin: '0 4px' }}>
                –
              </span>
              {formatTime(booking.endTime)}
            </Text>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            {booking.createdByRole === 'SERVICE_PROVIDER' && (
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: 'var(--bl-accent, #1677ff)',
                  background: 'var(--bl-accent-soft, #e7f1fe)',
                  borderRadius: 12,
                  padding: '2px 7px',
                  letterSpacing: 0.2,
                  textTransform: 'uppercase',
                }}
              >
                {t('provider:bookings.card.walkInBadge')}
              </div>
            )}
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                fontSize: 11,
                fontWeight: 700,
                color: config.color,
                background: config.color + '14',
                border: `1px solid ${config.color}26`,
                borderRadius: 20,
                padding: '3px 10px 3px 9px',
                whiteSpace: 'nowrap',
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: config.color,
                  flexShrink: 0,
                  boxShadow: isPending ? `0 0 0 3px ${config.color}24` : 'none',
                }}
              />
              {statusLabel}
            </div>
          </div>
        </div>

        {/* ─── Row 2: Client (avatar + name) + amount ───────────────── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
            {/* Avatar */}
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${config.color}18 0%, ${config.color}28 100%)`,
                color: config.color,
                fontSize: 13,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                border: `1px solid ${config.color}1a`,
                letterSpacing: -0.2,
              }}
            >
              {avatarLetters || '?'}
            </div>

            <div style={{ minWidth: 0, flex: 1 }}>
              {clientDisplay && (
                <Text
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: 'var(--bl-text-strong, #0f172a)',
                    display: 'block',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    lineHeight: 1.25,
                  }}
                >
                  {clientDisplay}
                </Text>
              )}
              {(booking.serviceName || booking.durationMinutes) && (
                <Text
                  style={{
                    fontSize: 12,
                    color: 'var(--bl-text-muted, #6b7280)',
                    display: 'block',
                    marginTop: 1,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {[
                    booking.serviceName,
                    booking.durationMinutes
                      ? t('common:units.minutesShort', { count: booking.durationMinutes })
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Text>
              )}
            </div>
          </div>

          {/* Amount */}
          {booking.totalAmount !== null && booking.totalAmount !== undefined && (
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 3, justifyContent: 'flex-end' }}>
                <Text
                  style={{
                    fontSize: 17,
                    fontWeight: 800,
                    color: isCompleted ? '#1f8b4c' : 'var(--bl-text-strong, #0f172a)',
                    letterSpacing: -0.4,
                    lineHeight: 1.1,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {booking.totalAmount.toLocaleString('uz-UZ')}
                </Text>
                <Text
                  style={{
                    fontSize: 11,
                    fontWeight: 500,
                    color: 'var(--bl-text-muted, #9ca3af)',
                  }}
                >
                  {t('provider:common.currency')}
                </Text>
              </div>
            </div>
          )}
        </div>

        {/* ─── Row 3: Sub badges (guest, attendance, payment) ───────── */}
        {(booking.customerType === 'GUEST' ||
          booking.customerAttendanceStatus ||
          (booking.totalAmount !== null &&
            booking.totalAmount !== undefined &&
            booking.totalAmount > 0)) && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              marginTop: 10,
              alignItems: 'center',
            }}
          >
            {booking.customerType === 'GUEST' && <GuestCustomerBadge />}
            {booking.customerAttendanceStatus && (
              <AttendanceStatusBadge status={booking.customerAttendanceStatus} size="sm" />
            )}
            {booking.totalAmount !== null &&
              booking.totalAmount !== undefined &&
              booking.totalAmount > 0 && (
                <PaymentStatusBadge status={booking.paymentStatus} size="sm" />
              )}
          </div>
        )}

        {/* ─── Cancellation reason (only if cancelled) ──────────────── */}
        {isCanceled && cancellationReasonDisplay && (
          <div
            style={{
              marginTop: 10,
              padding: '8px 10px',
              borderRadius: 10,
              background: 'rgba(255,77,79,0.06)',
              border: '1px solid rgba(255,77,79,0.15)',
            }}
          >
            <Text
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: '#cf1322',
                display: 'block',
                textTransform: 'uppercase',
                letterSpacing: 0.4,
              }}
            >
              {t('provider:bookings.card.cancellationReasonLabel')}
            </Text>
            <Text
              style={{
                fontSize: 12,
                fontWeight: 500,
                color: 'var(--bl-text-strong, #111)',
                display: 'block',
                marginTop: 2,
                lineHeight: 1.35,
              }}
            >
              {cancellationReasonDisplay}
            </Text>
          </div>
        )}

        <BookingQuickActions booking={booking} onActionChange={setIsActionOpen} />
      </div>
    </div>
  );
}
